import { cn } from '@repo/tailwind-config/tailwind.functions'
import { CellContext, TableRequiredFields } from '@repo/ui/data-table'
import DateStringInput from '@repo/ui/date-string-input'
import Input from '@repo/ui/input'
import SelectInput from '@repo/ui/select-input'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import useTranslate from '../../../lib/hooks/use-translate'
import { EditableTableColumn } from '.'

type EditableCellProps<TData extends TableRequiredFields> = {
  row: CellContext<TData, unknown>['row']
  column: EditableTableColumn<TData>
  onValueChange: (
    rowId: number,
    columnKey: keyof TData,
    value: number | string | undefined
  ) => void
}

export const EditableCell = <TData extends TableRequiredFields>({
  row,
  column,
  onValueChange
}: EditableCellProps<TData>) => {
  const columnKey = column.accessorKey as keyof TData
  const currentValue = row.original[columnKey] as number | string
  const [inputValue, setInputValue] = useState<number | string>(currentValue)
  const [error, setError] = useState<string | undefined>(undefined)
  const { t } = useTranslate()

  useEffect(() => {
    setInputValue(currentValue)
    setError(undefined)
  }, [currentValue])

  // Option 1: Using a switch statement for better readability
  const getTypedValue = () => {
    switch (column.inputType) {
      case 'text':
        return inputValue ? String(inputValue) : undefined
      case 'number': {
        if (inputValue === '') return undefined
        const n = Number(inputValue)
        if (Number.isNaN(n)) return undefined
        return n
      }
      default:
        return inputValue
    }
  }

  const handleBlur = () => {
    const typedValue = getTypedValue()
    const id = row.original.id

    // Validate numeric ranges on blur; if invalid, toast + revert and don't submit
    if (column.inputType === 'number' && typeof typedValue === 'number') {
      if (typeof column.min === 'number' && typedValue < column.min) {
        const msg = `${t('editable.table.min.value.error')} ${column.min}`
        toast.error(msg)
        // revert to previous value
        setInputValue(currentValue)
        return
      }
      if (typeof column.max === 'number' && typedValue > column.max) {
        const msg = `${t('editable.table.max.value.error')} ${column.max}`
        toast.error(msg)
        // revert to previous value
        setInputValue(currentValue)
        return
      }
    }

    if (id) {
      onValueChange(id, columnKey, typedValue)
      column.onValueChange?.({ id, value: typedValue })
      // Clear error on successful commit
      setError(undefined)
    }
  }

  if (column.inputType === 'select')
    return (
      <SelectInput
        label={column.inputLabel ?? column.header}
        searchable
        labelClassName={column.cellClassName}
        inputClassName={column.inputClassName}
        dropDownPosition="above"
        options={column.options}
        optional={column.optional}
        optionalText={t('editable.table.select.none')}
        onChange={(value) => {
          setInputValue(value)
          const id = row.original.id
          if (id) {
            onValueChange(id, columnKey, value)
            column.onValueChange?.({ id, value })
          }
        }}
        selectedOption={
          column.options
            ? column.options.find((option) => option.value === currentValue)
            : undefined
        }
      />
    )

  if (column.inputType === 'date')
    return (
      <DateStringInput
        mode="over"
        wrapperClassName={column.inputWrapperClassName}
        value={inputValue as string}
        onChange={(value) => {
          if (value) {
            setInputValue(value)
            const id = row.original.id
            if (id) {
              onValueChange(id, columnKey, value)
              column.onValueChange?.({ id, value })
            }
          }
        }}
      />
    )

  return (
    <Input
      className={'px-0.5'}
      inputClassName={cn('text-center', column.inputClassName)}
      type={column?.inputType ?? 'text'}
      value={inputValue?.toString()}
      wrapperClassName={cn('w-12', column.inputWrapperClassName)}
      onBlur={handleBlur}
      onChange={(e) => {
        if (column.inputType === 'number') {
          const raw = e.target.value
          if (raw === '') {
            setInputValue('')
            setError(undefined)
            return
          }
          const parsed = Number(raw)
          if (Number.isNaN(parsed)) {
            setInputValue(raw)
            return
          }
          setError(undefined)
          // Accept valid value
          setInputValue(raw)
          return
        }
        setInputValue(e.target.value)
      }}
      errorMessage={error}
    />
  )
}
